"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useEffect } from "react";
import mapboxgl, { LngLat, LngLatLike, MapOptions } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { FeatureCollection, Geometry } from "geojson";
import { toaster } from "@/components/ui/toaster";
import { LayerToggleObjProps } from "./address-mapper";
const defaultCoords: LngLatLike = [-122.408020683, 37.801698301]; // TODO: dedupe with address-mapper default coords; consider centralizing in a constants file if we need to use in more places
const mapOptions: Omit<MapOptions, "container"> = {
  style: "mapbox://styles/mapbox/standard",
  center: defaultCoords,
  zoom: 12.1,
  // Start with more zoomed-out view but not too far
  minZoom: 11,
  // Allow users to zoom out more
  maxZoom: 15,
  // Increase max zoom to allow closer inspection
  maxBounds: [[-122.6, 37.65],
  // Southwest coordinates
  [-122.25, 37.85] // Northeast coordinates
  ],
  dragRotate: false,
  // turn off rotation on drag
  touchPitch: false,
  // turn off pitch change w/touch
  touchZoomRotate: true,
  // turn on zoom/rotate w/touch
  config: {
    // Initial configuration for the Mapbox Standard style set above. By default, its ID is `basemap`.
    basemap: {
      // 'default', 'faded', or 'monochrome'
      theme: "monochrome"
    }
  }
};
interface MapProps {
  coordinates: number[];
  softStoryData: FeatureCollection<Geometry>;
  tsunamiData: FeatureCollection<Geometry>;
  liquefactionData: FeatureCollection<Geometry>;
  layerToggleObj: LayerToggleObjProps;
}
const Map: React.FC<MapProps> = t0 => {
  const $ = _c(15);
  const {
    coordinates: t1,
    softStoryData,
    tsunamiData,
    liquefactionData,
    layerToggleObj
  } = t0;
  const coordinates = t1 === undefined ? defaultCoords : t1;
  const mapContainerRef = useRef(null);
  const mapRef = useRef(undefined);
  const markerRef = useRef(undefined);
  let t2;
  if ($[0] !== layerToggleObj.layerId || $[1] !== layerToggleObj.toggleState) {
    t2 = () => {
      if (!mapRef.current) {
        return;
      }
      const map = mapRef.current;
      const layerId = layerToggleObj.layerId;
      if (layerId && map.getLayer(layerId)) {
        const newVisibility = layerToggleObj.toggleState ? "visible" : "none";
        map.setLayoutProperty(layerId, "visibility", newVisibility);
      }
    };
    $[0] = layerToggleObj.layerId;
    $[1] = layerToggleObj.toggleState;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleToggleLayers = t2;
  let t3;
  let t4;
  if ($[3] !== coordinates || $[4] !== liquefactionData || $[5] !== softStoryData || $[6] !== tsunamiData) {
    t3 = () => {
      const mapboxToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
      if (!mapContainerRef.current || !mapboxToken) {
        if (!toaster.isVisible("no-token")) {
          toaster.create({
            id: "no-token",
            description: "Mapbox access token or container is not set!",
            type: "error",
            duration: 5000,
            closable: true
          });
        }
        console.error("Mapbox access token or container is not set!");
        return;
      }
      mapboxgl.accessToken = mapboxToken;
      if (!mapRef.current) {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          ...mapOptions
        });
        const map_0 = mapRef.current;
        map_0.touchZoomRotate.disableRotation();
        const nav = new mapboxgl.NavigationControl({
          showCompass: false
        });
        map_0.addControl(nav, "bottom-right");
        map_0.on("load", () => {
          const el = document.createElement("div");
          const addressLngLat = new LngLat(coordinates[0], coordinates[1]);
          const addressMarker = new mapboxgl.Marker({
            anchor: "bottom",
            element: el,
            className: "marker"
          }).setLngLat(addressLngLat).addTo(map_0);
          markerRef.current = addressMarker;
          map_0.addSource("seismic", {
            type: "geojson",
            data: liquefactionData
          });
          map_0.addSource("tsunami", {
            type: "geojson",
            data: tsunamiData
          });
          map_0.addSource("soft-stories", {
            type: "geojson",
            data: softStoryData
          });
          map_0.addLayer({
            id: "tsunamiLayer",
            source: "tsunami",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": "#63B3ED",
              "fill-opacity": 0.25
            }
          });
          map_0.addLayer({
            id: "seismicLayer",
            source: "seismic",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": "#F6AD55",
              "fill-opacity": 0.5
            }
          });
          map_0.addLayer({
            id: "softStoriesLayer",
            source: "soft-stories",
            type: "circle",
            slot: "middle",
            paint: {
              "circle-radius": 4.5,
              "circle-stroke-width": 1,
              "circle-stroke-color": "#FFFFFF",
              "circle-color": "#A0AEC0"
            }
          });
          map_0.on("error", _temp);
        });
      } else {
        const map_1 = mapRef.current;
        const addressLngLat_0 = new LngLat(coordinates[0], coordinates[1]);
        map_1.panTo(addressLngLat_0);
        markerRef.current?.setLngLat(addressLngLat_0);
        return;
      }
    };
    t4 = [coordinates, liquefactionData, softStoryData, tsunamiData];
    $[3] = coordinates;
    $[4] = liquefactionData;
    $[5] = softStoryData;
    $[6] = tsunamiData;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== handleToggleLayers || $[10] !== layerToggleObj.layerId) {
    t5 = () => {
      if (layerToggleObj.layerId != "") {
        handleToggleLayers();
      }
    };
    $[9] = handleToggleLayers;
    $[10] = layerToggleObj.layerId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== layerToggleObj) {
    t6 = [layerToggleObj];
    $[12] = layerToggleObj;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div ref={mapContainerRef} style={{
      width: "100%",
      height: "100%"
    }} />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
export default Map;
function _temp(e) {
  if (e.error && e.error.message.includes("access token")) {
    if (!toaster.isVisible("invalid-token")) {
      toaster.create({
        id: "invalid-token",
        description: "Invalid Mapbox access token!",
        type: "error",
        duration: 5000,
        closable: true
      });
    }
    console.error("Invalid Mapbox token:", e.error);
  }
}